import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Loader2, Mic, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  createMicrophone,
  discoverMicrophones,
  type DiscoveredAudioDevice,
  type MicrophoneKind,
  type MicrophoneUpsert,
} from '@/api/microphones';
import { extractApiError } from '@/utils/apiError';

interface Props {
  zoneId: number | string;
  onCreated: () => void;
  onCancel: () => void;
}

/**
 * Inline new-microphone form used by `AddDeviceDropdown → Microphone`.
 *
 * The "Discover" button runs an `arecord -l` self-probe on the zone's
 * Jetson (`discoverMicrophones`) and lists the capture devices. Picking
 * one fills the ALSA address + card identity; the operator can also type
 * an `hw:X,Y` address by hand. Save POSTs the record, which lights up the
 * speech-analytics tab via `Zone.has_mic`.
 *
 * Режим «Сетевой (роутер)» — микрофон в роутере у кассы точки без джетсона:
 * одно поле адреса `tcp://<wg-ip>:5555`, без Discover; запись пишет централь.
 */
export default function NewMicrophoneCard({ zoneId, onCreated, onCancel }: Props) {
  const { t } = useT();
  const [kind, setKind] = useState<MicrophoneKind>('usb');
  const [alsaDevice, setAlsaDevice] = useState('');
  const [netAddress, setNetAddress] = useState('');
  const [selected, setSelected] = useState<DiscoveredAudioDevice | null>(null);
  const [devices, setDevices] = useState<DiscoveredAudioDevice[] | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const discover = async () => {
    setDiscovering(true);
    setDiscoverError(null);
    setDevices(null);
    try {
      const { data } = await discoverMicrophones(zoneId);
      setDevices(data.devices);
    } catch (e) {
      setDiscoverError(extractApiError(e, t('error.general')));
    } finally {
      setDiscovering(false);
    }
  };

  const pickDevice = (d: DiscoveredAudioDevice) => {
    setSelected(d);
    setAlsaDevice(d.alsa_device);
  };

  const save = async (payload: MicrophoneUpsert) => {
    setSubmitting(true);
    try {
      await createMicrophone(zoneId, payload);
      onCreated();
    } catch (e) {
      if (isAxiosError(e)) {
        setError(extractApiError(e, t('error.general')));
      } else {
        setError(t('error.general'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (kind === 'network') {
      const address = netAddress.trim();
      if (!/^tcp:\/\/[A-Za-z0-9.-]+:\d{1,5}$/.test(address)) {
        setError(t('equipment.newMic.addressInvalid'));
        return;
      }
      await save({ alsa_device: address, channels: 1 });
      return;
    }
    if (!alsaDevice.trim()) {
      setError(t('equipment.newMic.alsaDeviceRequired'));
      return;
    }
    // Keep card identity only when the typed device still matches the
    // picked one — a hand-edited address shouldn't carry stale metadata.
    const matched = selected && selected.alsa_device === alsaDevice.trim() ? selected : null;
    const payload: MicrophoneUpsert = {
      alsa_device: alsaDevice.trim(),
      card_index: matched?.card_index ?? undefined,
      device_index: matched?.device_index ?? undefined,
      card_id: matched?.card_id ?? undefined,
      card_name: matched?.card_name ?? undefined,
    };
    await save(payload);
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Mic className="size-4" /> {t('equipment.newMic.title')}
        </h2>

        <div className="space-y-3 text-sm">
          <ToggleGroup
            value={kind}
            onChange={next => {
              if (next) {
                setKind(next as MicrophoneKind);
                setError(null);
              }
            }}
          >
            <ToggleGroupItem value="usb">{t('equipment.newMic.kindUsb')}</ToggleGroupItem>
            <ToggleGroupItem value="network">{t('equipment.newMic.kindNetwork')}</ToggleGroupItem>
          </ToggleGroup>

          {kind === 'network' ? (
            <div className="space-y-1.5">
              <Label htmlFor="new-mic-address">{t('equipment.newMic.address')}</Label>
              <Input
                id="new-mic-address"
                value={netAddress}
                onChange={e => { setNetAddress(e.target.value); }}
                placeholder="tcp://10.99.0.N:5555"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">{t('equipment.newMic.addressHint')}</p>
            </div>
          ) : (
            <>
              <div>
                <Button variant="outline" size="sm" onClick={discover} disabled={discovering}>
                  {discovering ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Search className="size-3.5" />
                  )}
                  {t('equipment.newMic.discover')}
                </Button>
              </div>

              {discoverError && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {discoverError}
                </div>
              )}

              {devices && devices.length === 0 && (
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  {t('equipment.newMic.noDevices')}
                </div>
              )}

              {devices && devices.length > 0 && (
                <div className="space-y-1.5">
                  <Label>{t('equipment.newMic.discovered')}</Label>
                  <div className="flex flex-col gap-1.5">
                    {devices.map(d => (
                      <button
                        key={d.alsa_device}
                        type="button"
                        onClick={() => pickDevice(d)}
                        className={cn(
                          'flex items-center justify-between rounded-md border px-3 py-2 text-left transition-colors',
                          selected?.alsa_device === d.alsa_device
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/40',
                        )}
                      >
                        <span className="text-sm">{d.card_name || d.device_name || d.card_id}</span>
                        <span className="font-mono text-xs text-muted-foreground">{d.alsa_device}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="new-mic-alsa">{t('equipment.newMic.alsaDevice')}</Label>
                <Input
                  id="new-mic-alsa"
                  value={alsaDevice}
                  onChange={e => { setAlsaDevice(e.target.value); }}
                  placeholder="hw:2,0"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">{t('equipment.newMic.alsaHint')}</p>
              </div>
            </>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              {t('common.save')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
